import { replace } from 'redux-first-history';
import { createAppSlice } from '.';
import {
  createCluster as createApiCluster,
  getClusters,
  getClustersPartners,
  linkCluster,
  linkUserToClusterByMail,
  unlinkCluster as unlinkApiCluster,
  updateCluster as updateApiCluster,
} from '../api/api';
import { UserRole } from '../constants';
import { ClusterActivity, ClusterPlan, ClusterType, PluginName } from '../constants/clusterConstants';
import { ItemWithPermissions } from '../utils/permissions';
import { PartialWithRequired } from '../utils/types';
import { alerting, success } from './snacks';
import { userLinkedWithSuccess } from './users';

type PluginId = string;
type Plugin = {
  activated: boolean;
  expired_at: string;
  id: PluginId;
  option: PluginName;
};

export type ClusterId = number;
export type Cluster = ItemWithPermissions & {
  id: ClusterId;
  name: string;
  address?: string;
  cluster_type: ClusterType;
  color?: string;
  role?: UserRole;
  activities?: ClusterActivity[];
  count_admin: number;
  count_parcels: number;
  size_parcels: number;
  email?: string;
  from_application: string;
  is_demo: boolean;
  linked_partner: string;
  parent_cluster_ids?: ClusterId[];
  phone: string;
  plan: ClusterPlan;
  plugins: Plugin[];
  siren: string;
};

type ClusterPartner = {
  cluster_id: ClusterId;
  linked_from: string;
  partner: string;
};
type ClusterPartners = {
  cluster_id: ClusterId;
  linked_from: string[];
  partner: string[];
};

type State = {
  isFetching: boolean;
  clusterById: Record<ClusterId, Cluster>;
  clustersIds: ClusterId[];
  clusterPartners: Record<ClusterId, ClusterPartners>;
  clusterPartnersFetched: boolean;
};

const initialState: State = {
  isFetching: false,
  clusterById: {},
  clustersIds: [],
  clusterPartners: {},
  clusterPartnersFetched: false,
};

function addClusterToState(state: State, cluster: Cluster) {
  state.clustersIds = state.clustersIds.filter((id) => cluster.id !== id);
  state.clustersIds.push(cluster.id);
  state.clusterById[cluster.id] = { ...state.clusterById[cluster.id], ...cluster };
}

const clustersSlice = createAppSlice({
  name: 'clusters',
  initialState,
  reducers: (create) => ({
    fetchClusters: create.asyncThunk(
      async (_, { dispatch }) => {
        const clusters = (await alerting(getClusters, { dispatch })) as Cluster[];
        if (clusters.length === 0) {
          dispatch(replace('/unlinked'));
        }
        return clusters;
      },
      {
        pending: (state) => {
          state.isFetching = true;
        },
        fulfilled: (state, { payload }) => {
          for (const cluster of payload) {
            addClusterToState(state, cluster);
          }
        },
        settled: (state) => {
          state.isFetching = false;
        },
      },
    ),
    createCluster: create.asyncThunk(
      async (
        payload: {
          cluster: Partial<Cluster>;
          clusterIds: ClusterId[];
          email?: string;
          redirectToAdmin?: boolean;
        },
        { dispatch },
      ) => {
        const createdCluster = (await alerting(() => createApiCluster(payload.cluster), { dispatch })) as Cluster;

        for (const clusterId of payload.clusterIds) {
          await alerting(() => linkCluster(clusterId, createdCluster.id), { dispatch });
        }

        if (payload.email) {
          const link = await alerting(
            () => linkUserToClusterByMail({ email: payload.email, clusterID: createdCluster.id }),
            { dispatch },
          );
          success('Clusters.user_linked_successfully', { params: { email: payload.email }, dispatch });
          dispatch(userLinkedWithSuccess({ user: link, cluster_id: createdCluster.id, user_id: link.id }));
        }

        payload.redirectToAdmin && dispatch(replace('/admin'));
        return createdCluster;
      },
      {
        fulfilled: (state, { payload }) => {
          addClusterToState(state, payload);
        },
      },
    ),
    updateCluster: create.asyncThunk(
      async (payload: { cluster: PartialWithRequired<Cluster, 'id'>; withRedirection?: boolean }, { dispatch }) => {
        const updatedCluster = (await alerting(() => updateApiCluster(payload.cluster), { dispatch })) as Cluster;
        success('Admin.update_cluster_snack', { dispatch });
        payload.withRedirection !== false && dispatch(replace('/admin'));
        return updatedCluster;
      },
      {
        fulfilled: (state, { payload }) => {
          addClusterToState(state, payload);
        },
      },
    ),
    unlinkCluster: create.asyncThunk(
      async (payload: { parentId: ClusterId; childId: ClusterId }, { dispatch }) => {
        await alerting(() => unlinkApiCluster(payload.parentId, payload.childId), { dispatch });
        success('Admin.unlink_cluster', { dispatch });
        return payload.childId;
      },
      {
        fulfilled: (state, { payload }) => {
          state.clustersIds = state.clustersIds.filter((id) => id !== payload);
          delete state.clusterById[payload];
        },
      },
    ),
    fetchClusterPartners: create.asyncThunk(
      async (_, { dispatch }) => {
        const clusterPartners = ((await alerting(getClustersPartners, { dispatch })) || []) as ClusterPartner[];
        return clusterPartners;
      },
      {
        fulfilled: (state, { payload }) => {
          state.clusterPartners = {};
          for (const clusterPartner of payload) {
            state.clusterPartners[clusterPartner.cluster_id] ??= {
              cluster_id: clusterPartner.cluster_id,
              linked_from: [],
              partner: [],
            };
            state.clusterPartners[clusterPartner.cluster_id]!.linked_from.push(clusterPartner.linked_from);
            state.clusterPartners[clusterPartner.cluster_id]!.partner.push(clusterPartner.partner);
          }
          state.clusterPartnersFetched = true;
        },
      },
    ),
  }),
  selectors: {
    selectClustersById: (state) => state.clusterById,
    selectClusterPartners: (state) => state.clusterPartners,
    selectClusterIds: (state) => state.clustersIds,
    selectAreClustersFetching: (state) => state.isFetching,
    selectClusterPartnersFetched: (state) => state.clusterPartnersFetched,
  },
});

export const { fetchClusters, createCluster, updateCluster, unlinkCluster, fetchClusterPartners } =
  clustersSlice.actions;
export const {
  selectClustersById,
  selectClusterIds,
  selectClusterPartners,
  selectAreClustersFetching,
  selectClusterPartnersFetched,
} = clustersSlice.selectors;

export default clustersSlice.reducer;
